// Props of the Dashboard (all markets) and the market Overview.
// Shapes mirror Home::MarketSnapshot, Home::CompanyRevenue,
// Home::PurchaseSummary and Home::MarketOverview.

export type PlanState = "published" | "revising" | "draft" | "none"

export interface PlanSummary {
  state: PlanState
  name?: string
  version_number?: number | null
  begin_date?: string
  end_date?: string
  /** Null when the plan is not published, or does not cover the whole market. */
  target?: number | null
  /** Revenue this year, January to today, whatever the plan's own dates. */
  revenue?: number | null
}

/** Products on the Push review list; no_movement had nothing go out in 60 days. */
export interface ExcessCounts {
  total: number
  no_movement: number
}

export interface MarketSnapshot {
  id: string
  slug: string
  name: string
  currency: string
  revenue_this_month: number
  /** Against the same days of last month; null when those days sold nothing. */
  change_pct: number | null
  revenue_this_year: number
  monthly: { key: string; revenue: number }[]
  plan: PlanSummary
  excess: ExcessCounts
  need_buying: number
}

export interface CompanyRevenue {
  display_currency: string
  rate_date: string | null
  /** Units per 1 USD for each currency involved that has a stored rate. */
  rates: Record<string, number>
  unconverted_markets: string[]
  /** Null while any market has no exchange rate. */
  revenue_this_month: number | null
  change_pct: number | null
  revenue_this_year: number | null
  /** Only the markets whose plan has a target; null while none has one or a counted market has no rate. */
  plan_progress: { target: number | null; revenue: number | null; not_counted: string[] }
  monthly: { key: string; markets: Record<string, number> }[]
  /** This month per channel of every market, converted; markets with no rate left out. */
  channels: { name: string; market: string; revenue: number }[]
  need_buying: number
  excess: ExcessCounts
}

/** One of a market's biggest orders on the Purchase Plan. */
export interface TopBuy {
  product_external_id: string
  name: string
  quantity: number
}

/** A product on the Push review list; see Push::Review::Board for excess. */
export interface ExcessProduct {
  product_external_id: string
  name: string
  /** Units that won't sell within the cut-off, counting dated incoming orders. */
  excess_units: number
  /** excess_units × buying price, in the market's currency. */
  excess_value: number
}

export interface MarketOverview {
  monthly: { key: string; actual: number | null; plan: number | null }[]
  channels: { name: string; revenue: number }[]
  most_excess: ExcessProduct[]
  planning: { slug: string; months_total: number; months_filled: number } | null
  data_updated: { label: string; last_synced_at: string | null }[]
}

/** "2026-03-01" → "Mar" */
export function monthLabel(key: string): string {
  return new Date(key + "T00:00:00").toLocaleDateString("en-US", { month: "short" })
}

/** "41 overstock · 52 slow · 19 dead" */
export function excessBreakdown(excess: ExcessCounts): string {
  return `${excess.no_movement.toLocaleString("en-US")} with no movement`
}
